/**
 * Clinic switcher — the dropdown behind the chevron in the sidebar header.
 *
 * ## Who sees the chevron
 *
 * Two different conditions, and conflating them creates a chicken-and-egg:
 *
 *   - `canSwitch` (org owner with >1 clinic) governs whether there is anything
 *     to switch BETWEEN.
 *   - `isAdmin` governs whether the user can CREATE a branch.
 *
 * Gating the whole control on `canSwitch` would hide "Add a clinic" from the
 * single-clinic Admin who is the only person able to use it — so nobody could
 * ever reach two clinics. The chevron therefore shows for any Admin; a
 * single-clinic Admin gets a one-row list and the Add action.
 *
 * Doctors and Receptionists see nothing at all: they are pinned to their clinic
 * and cannot create one.
 *
 * Switching is heavyweight on purpose — it drains the offline queue, wipes the
 * local PHI cache and reloads the app (see ClinicContext). The step-by-step
 * overlay for that lives at App level in ClinicSwitchOverlay.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ClinicSwitchBlockedError,
  useClinic,
} from "../../contexts/ClinicContext";
import { useRole } from "../../hooks/useRole";
import { AccessibleClinic } from "../../services/organization.service";
import { ChevronDownIcon } from "../icons";

/** Two-letter monogram for the clinic tile, e.g. "Rajouri Garden" → "RG". */
function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const TILE_COLORS = [
  "bg-indigo-600",
  "bg-emerald-600",
  "bg-violet-600",
  "bg-sky-600",
  "bg-rose-600",
  "bg-amber-600",
];

/** Stable colour per clinic — same tile colour across sessions and devices. */
function tileColor(clinicId: string): string {
  let hash = 0;
  for (let i = 0; i < clinicId.length; i++) {
    hash = (hash * 31 + clinicId.charCodeAt(i)) >>> 0;
  }
  return TILE_COLORS[hash % TILE_COLORS.length];
}

function clinicSubtitle(clinic: AccessibleClinic): React.ReactNode {
  const parts: string[] = [];
  if (clinic.location) parts.push(clinic.location);
  parts.push(`${clinic.doctor_count} ${clinic.doctor_count === 1 ? "doctor" : "doctors"}`);

  if (!clinic.setup_complete) {
    return (
      <span className="text-amber-600">
        {parts.join(" · ")} · Setup incomplete
      </span>
    );
  }
  if (!clinic.is_active) {
    return (
      <span className="text-slate-400">{parts.join(" · ")} · Inactive</span>
    );
  }
  return <span className="text-slate-500">{parts.join(" · ")}</span>;
}

export const ClinicSwitcher: React.FC = () => {
  const {
    clinics,
    activeClinicId,
    organizationName,
    canSwitch,
    isOnline,
    isSwitching,
    switchClinic,
  } = useClinic();

  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isOpen]);

  // An Admin always gets the control, even with one clinic — otherwise the
  // only person who can add a second branch has no way to reach the action.
  // Doctors and Receptionists get nothing: pinned, and cannot create clinics.
  if (!canSwitch && !isAdmin) return null;

  const handleSelect = async (clinicId: string) => {
    if (clinicId === activeClinicId) {
      setIsOpen(false);
      return;
    }
    setError(null);
    try {
      // Resolves into a full page reload on success, so there is no success
      // path to handle here.
      await switchClinic(clinicId);
    } catch (err) {
      // Only the pre-flight offline rejection is surfaced inline. It throws
      // before any work starts, so no overlay ever appears and there would
      // otherwise be no feedback at all. Every later failure happens with the
      // overlay already up, and ClinicSwitchOverlay reports it there —
      // including which steps had already taken effect, which a one-line
      // message in this dropdown cannot convey.
      if (err instanceof ClinicSwitchBlockedError && err.reason === "offline") {
        setError(err.message);
        return;
      }
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((v) => !v)}
        disabled={isSwitching}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Switch clinic"
        className={`flex-shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 ${
          isOpen ? "bg-slate-100 text-slate-700" : ""
        }`}
      >
        <span
          className={`block transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        >
          <ChevronDownIcon />
        </span>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          role="menu"
          className="absolute left-3 right-3 top-[60px] z-[80] overflow-hidden rounded-2xl bg-white"
          style={{
            boxShadow:
              "0 8px 40px 0 rgba(0,0,0,0.14), 0 2px 8px 0 rgba(0,0,0,0.08)",
          }}
        >
          <div className="px-4 pb-2 pt-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Organisation
            </p>
            <p className="truncate text-[15px] font-semibold text-slate-900">
              {organizationName ?? "My clinics"}
            </p>
          </div>

          {!isOnline && (
            <p className="mx-4 mb-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
              You are offline. Switching clinics needs a connection — it syncs
              pending records and reloads this clinic&apos;s data.
            </p>
          )}

          <div className="max-h-[320px] overflow-y-auto px-2 pb-1">
            {clinics.map((clinic) => {
              const isActive = clinic.clinic_id === activeClinicId;
              return (
                <button
                  key={clinic.clinic_id}
                  role="menuitem"
                  onClick={() => handleSelect(clinic.clinic_id)}
                  disabled={isSwitching || (!isOnline && !isActive)}
                  className={`flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    isActive ? "bg-indigo-50" : "hover:bg-slate-50"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-xs font-semibold text-white ${tileColor(
                      clinic.clinic_id,
                    )}`}
                  >
                    {initials(clinic.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-[13px] font-semibold text-slate-900"
                      title={clinic.name}
                    >
                      {clinic.name}
                    </span>
                    <span className="block truncate text-[11px]">
                      {clinicSubtitle(clinic)}
                    </span>
                  </span>
                  {isActive && (
                    <svg
                      className="h-4 w-4 flex-shrink-0 text-indigo-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          {error && (
            <p className="mx-4 mb-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
              {error}
            </p>
          )}


          <div className="border-t border-slate-100 px-2 py-1.5">
            <button
              disabled={!isOnline}
              title={!isOnline ? "Needs a connection" : undefined}
              onClick={() => {
                setIsOpen(false);
                // The full onboarding flow, in branch mode — a new clinic
                // needs its own documents and plan, which is the same journey
                // a first clinic takes. A cut-down modal would have created
                // half-configured branches.
                navigate("/onboarding/clinic-details?mode=branch");
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
            >
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                viewBox="0 0 24 24"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add a clinic
            </button>
            <button
              disabled
              title="Coming soon — org billing view is not wired up yet"
              className="flex w-full cursor-not-allowed items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] text-slate-400"
            >
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <rect x="3" y="7" width="18" height="13" rx="2" />
                <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Manage clinics &amp; billing
            </button>
          </div>
        </div>
      )}

      {/*
        The blocking switch overlay is NOT rendered here. It lives at App level
        (ClinicSwitchOverlay) so it survives this dropdown closing and the
        mobile drawer unmounting — a switch mid-flight must not lose its
        progress UI because the sidebar re-rendered.
      */}

    </>
  );
};

export default ClinicSwitcher;
